/**
 * Step 4 — "Connect your stack": Gmail/Outlook + Calendar status and meeting
 * recorder selection.
 *
 * The top card is provider-aware: it detects Google vs. Microsoft from the
 * user's signup social account, fetches real connection status for that
 * provider, and drives the OAuth connect flow. Meeting recorder selections
 * just live in flow state.
 */
import { Mail01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import {
  GoogleBrandMark,
  MicrosoftBrandMark,
} from '@/components/integrations/ProviderBrandMarks';
import { useGmailStatus } from '@/hooks/integrations/google';
import { useOutlookStatus } from '@/hooks/integrations/microsoft';
import { useOAuthConnect } from '@/hooks/integrations/useOAuthConnect';
import { useUserProfileData } from '@/hooks/useUserProfileData';
import { Button } from '@/lib/base/buttons/button';
import { SkipButton } from '@/lib/self-service/components/SkipButton';
import { computeCardVisuals } from '@/lib/self-service/components/steps/stackStepCardVisuals';
import { StackStepConnectionCard } from '@/lib/self-service/components/steps/StackStepConnectionCard';
import { StackStepRecorderSelections } from '@/lib/self-service/components/steps/StackStepRecorderSelections';
import { useSelfServiceFlowState } from '@/lib/self-service/flowState';
import { useToggleInArray } from '@/lib/self-service/hooks/useToggleInArray';

const PROVIDER_COPY = {
  google: {
    title: 'Gmail + Calendar',
  },
  microsoft: {
    title: 'Outlook + Calendar',
  },
} as const;

const UNKNOWN_PROVIDER_TITLE = 'Email + Calendar';

type SignupProvider = 'google' | 'microsoft' | 'unknown';

/**
 * Detect the signup provider from the profile's social accounts. Returns
 * 'unknown' when neither a Google nor a Microsoft account is present — the
 * caller must NOT default to Google, or it would push an unserviceable account
 * through the Gmail OAuth flow.
 */
function detectSignupProvider(
  socialAccounts: ReadonlyArray<{ provider: string }> | undefined
): SignupProvider {
  if (socialAccounts?.some(a => a.provider === 'microsoft')) {
    return 'microsoft';
  }
  if (socialAccounts?.some(a => a.provider === 'google')) {
    return 'google';
  }
  return 'unknown';
}

type ConnectionCardStatus =
  | 'no-org'
  | 'profile-error'
  | 'loading'
  | 'unknown-provider'
  | 'status-error'
  | 'connected'
  | 'not-connected';

/**
 * Single source of truth for the connection card's state. Order matters: an
 * unresolved or errored profile is handled BEFORE any provider-specific state,
 * because the provider is unknown until the profile request succeeds — guessing
 * one would send (e.g.) a Microsoft signup through the Google OAuth flow. A
 * resolved profile with NO supported (Google/Microsoft) social account is its
 * own 'unknown-provider' state: we suppress every provider-specific status and
 * Connect affordance rather than defaulting to Google and pushing the wrong
 * OAuth flow. A failed status request is its own retriable state, never a
 * silent "not-connected" (which would offer a duplicate Connect on a live
 * mailbox).
 */
function resolveConnectionCardStatus(args: {
  hasOrg: boolean;
  profileError: boolean;
  profileResolving: boolean;
  providerSupported: boolean;
  statusLoading: boolean;
  statusError: boolean;
  connected: boolean;
}): ConnectionCardStatus {
  if (!args.hasOrg) {
    return 'no-org';
  }
  if (args.profileError) {
    return 'profile-error';
  }
  if (args.profileResolving) {
    return 'loading';
  }
  if (!args.providerSupported) {
    return 'unknown-provider';
  }
  if (args.statusLoading) {
    return 'loading';
  }
  if (args.statusError) {
    return 'status-error';
  }
  return args.connected ? 'connected' : 'not-connected';
}

interface StackStepProps {
  onContinue: () => void;
}

export function StackStep({ onContinue }: StackStepProps) {
  const { state, updateState } = useSelfServiceFlowState();
  const orgIdStr = state.orgId != null ? String(state.orgId) : undefined;

  const {
    data: profile,
    isError: profileError,
    isFetching: profileFetching,
    refetch: refetchProfile,
  } = useUserProfileData();
  // The provider is only trustworthy once a FRESH profile request has SUCCEEDED.
  // The hook hydrates from persisted localStorage with `initialDataUpdatedAt: 0`,
  // so `profile` can be non-null but STALE while a background refetch is in
  // flight — trusting it then risks routing a Microsoft signup through the
  // Google OAuth flow. Gate on `!profileFetching` so provider detection waits
  // for the live response.
  const profileResolved = profile != null && !profileFetching;
  // Detect the signup provider explicitly. A resolved profile with neither a
  // Google nor a Microsoft social account is 'unknown' — we must NOT default to
  // Google, because that would enable the Gmail status/connect flow for an
  // account we can't service. `providerSupported` gates every provider-specific
  // affordance below.
  const detectedProvider: SignupProvider = profileResolved
    ? detectSignupProvider(profile.social_accounts)
    : 'unknown';
  const providerSupported = detectedProvider !== 'unknown';
  // The status/connect hooks require a concrete provider; the 'google' fallback
  // only wires the (disabled) hooks when the provider is unknown — the hooks'
  // `enabled` flags and cardStatus keep them inert until `providerSupported`.
  const provider: 'google' | 'microsoft' =
    detectedProvider === 'microsoft' ? 'microsoft' : 'google';

  const gmail = useGmailStatus(orgIdStr, {
    enabled: providerSupported && provider === 'google' && state.orgId != null,
  });
  const outlook = useOutlookStatus(orgIdStr, {
    enabled:
      providerSupported && provider === 'microsoft' && state.orgId != null,
  });
  const status = provider === 'google' ? gmail : outlook;
  // A mailbox that requires reauthorization is NOT working: the status API can
  // report `connected: true` alongside `needs_reconnect: true`. Treating that
  // as connected would show a "Connected" badge and hide the Connect (reconnect)
  // CTA, stranding the user on a non-working mailbox. Gate `connected` on both.
  const connected =
    status.data?.connected === true && status.data?.needs_reconnect !== true;
  const statusLoading = status.isLoading;
  // A failed status request is NOT "disconnected": treating it as such would
  // offer a duplicate Connect on top of a possibly-live connection.
  const statusError = providerSupported && status.error != null;
  const refetchStatus = status.refetch;

  const {
    connect,
    isConnecting,
    error: connectError,
    setError,
  } = useOAuthConnect(orgIdStr, provider);
  const handleConnect = useCallback(() => {
    void connect('private');
  }, [connect]);
  const handleDismissError = useCallback(() => setError(null), [setError]);
  const handleRetryProfile = useCallback(() => {
    void refetchProfile();
  }, [refetchProfile]);
  const handleRetryStatus = useCallback(() => {
    void refetchStatus();
  }, [refetchStatus]);

  const handleRecorderChange = useCallback(
    (next: string[]) => updateState({ recorders: next }),
    [updateState]
  );
  const handleRecorderToggle = useToggleInArray(
    state.recorders,
    handleRecorderChange
  );

  const cardStatus = resolveConnectionCardStatus({
    hasOrg: state.orgId != null,
    profileError,
    profileResolving: !profileResolved,
    providerSupported,
    statusLoading,
    statusError,
    connected,
  });

  const ProviderIcon =
    provider === 'microsoft' ? MicrosoftBrandMark : GoogleBrandMark;
  const providerIcon = providerSupported ? (
    <ProviderIcon className='size-8 shrink-0' />
  ) : (
    <Mail01 className='size-8 shrink-0 text-[var(--color-text-tertiary)]' />
  );
  const cardTitle = providerSupported
    ? PROVIDER_COPY[provider].title
    : UNKNOWN_PROVIDER_TITLE;

  const { cardSubtitle, statusTrailing } = useMemo(
    () =>
      computeCardVisuals({
        cardStatus,
        provider,
        isConnecting,
        onConnect: handleConnect,
        onRetryProfile: handleRetryProfile,
        onRetryStatus: handleRetryStatus,
      }),
    [
      cardStatus,
      provider,
      isConnecting,
      handleConnect,
      handleRetryProfile,
      handleRetryStatus,
    ]
  );

  return (
    <div className='flex flex-col gap-6'>
      <StackStepConnectionCard
        icon={providerIcon}
        title={cardTitle}
        subtitle={cardSubtitle}
        trailing={statusTrailing}
        showOrgHint={state.orgId == null}
        connectError={connectError}
        onDismissError={handleDismissError}
      />
      <StackStepRecorderSelections
        selectedIds={state.recorders}
        onToggle={handleRecorderToggle}
      />

      <div className='flex flex-col gap-4'>
        <Button size='lg' className='w-full' onClick={onContinue}>
          Continue
        </Button>
        <SkipButton onSkip={onContinue} />
      </div>
    </div>
  );
}
